import { useMemo, type ReactElement } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { usePluginUsers, type PluginIdentity } from '@/hooks/api/usePlugins';
import { PluginSourceBadge } from '@/components/shared/plugins/PluginSourceBadge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { InventoryUserListDialog } from './InventoryUserListDialog';

interface PluginUserListModalProps {
  organizationId: string;
  /**
   * (pluginName, marketplace) for the selected row, or `null` when no row is
   * selected. Two rows with the same pluginName but different marketplaces
   * are different plugins (see the list endpoint's aggregation), so the
   * drill-down needs both pieces.
   */
  plugin: PluginIdentity | null;
  pluginDisplayName?: string | null;
  /**
   * Optional per-version filter.
   * - `undefined` → show all users for this plugin (plugin-level drill-down).
   * - `null`      → show only users whose installed version is unknown.
   * - string      → show only users on that exact version.
   */
  versionFilter?: string | null;
  onClose: () => void;
}

/**
 * Modal dialog showing users who have a specific plugin installed.
 * When `versionFilter` is provided, the user list is filtered client-side to
 * that version.
 */
export function PluginUserListModal({
  organizationId,
  plugin,
  pluginDisplayName,
  versionFilter,
  onClose,
}: PluginUserListModalProps): ReactElement {
  const { data, isLoading, error } = usePluginUsers(organizationId, plugin);

  const fetchedUsers = data?.users;
  const users = useMemo(() => {
    if (!fetchedUsers) {
      return [];
    }
    if (versionFilter === undefined) {
      return fetchedUsers;
    }
    return fetchedUsers.filter(user => user.version === versionFilter);
  }, [fetchedUsers, versionFilter]);

  const titleLabel = pluginDisplayName || plugin?.pluginName || '';
  const versionLabel =
    versionFilter === undefined
      ? null
      : versionFilter === null
        ? 'unknown version'
        : `v${versionFilter}`;

  return (
    <InventoryUserListDialog
      open={plugin !== null}
      onClose={onClose}
      title={
        <>
          Users with <span className="font-mono">{titleLabel}</span>
          {versionLabel && (
            <span className="text-muted-foreground font-normal">
              {' '}
              ({versionLabel})
            </span>
          )}
        </>
      }
      columns={[
        { label: 'User' },
        { label: 'Sources' },
        { label: 'Version' },
        { label: 'Last Seen' },
      ]}
      isLoading={isLoading}
      error={error}
      isEmpty={users.length === 0}
      emptyMessage="No users found with this plugin"
    >
      {users.map(user => (
        <TableRow key={`${user.userId}-${user.version ?? '_null'}`}>
          <TableCell>
            <div>
              <div className="font-medium">{user.name || 'Unknown User'}</div>
              {user.email && (
                <div className="text-sm text-muted-foreground">
                  {user.email}
                </div>
              )}
            </div>
          </TableCell>
          <TableCell>
            <div className="flex flex-wrap gap-1">
              {user.sources.map(src => (
                <PluginSourceBadge key={src} source={src} />
              ))}
            </div>
          </TableCell>
          <TableCell className="font-mono text-sm">
            {user.version ?? (
              <span className="text-muted-foreground">(unknown)</span>
            )}
          </TableCell>
          <TableCell className="text-muted-foreground">
            <RelativeTimeWithTooltip date={user.lastSeenAt} />
          </TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}
